<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import DiamondDetailCaratSize from "@/components/diamonds/details/carat/DiamondDetailCaratSize.vue";

const { t } = useI18n();

const props = withDefaults(defineProps<{
  carat?: number | { min: number, max: number };
  form?: string;
  width?: number;
  length?: number;
  mobileMode?: boolean;
}>(), {
  carat: 0.5,
  form: 'BR',
  width: 5,
  length: 5
});

const isRange = computed(() => typeof props.carat === 'object' && props.carat !== null);

const minCarat = computed(() => {
  if (isRange.value) {
    return (props.carat as { min: number, max: number }).min;
  }
  return (props.carat as number) || 0;
});

const maxCarat = computed(() => {
  if (isRange.value) {
    return (props.carat as { min: number, max: number }).max;
  }
  return (props.carat as number) || 0;
});

const currentCarat = ref(minCarat.value);

const isMobile = ref(window.innerWidth < 769);
const updateMobile = () => {
  isMobile.value = window.innerWidth < 769;
};

const isMobileMode = computed(() => props.mobileMode || isMobile.value);

watch(minCarat, (newVal) => {
  currentCarat.value = newVal;
});

const displayDimensions = computed(() => {
  if (minCarat.value === 0) return { width: 0, length: 0 };
  const scale = Math.pow(currentCarat.value / minCarat.value, 1 / 3);
  return {
    width: props.width * scale,
    length: props.length * scale,
  };
});

const sliderRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);

const updateCaratFromEvent = (e: MouseEvent | TouchEvent) => {
  if (!sliderRef.value) return;
  const rect = sliderRef.value.getBoundingClientRect();
  const clientX = "touches" in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
  const offsetX = clientX - rect.left;
  let percentage = offsetX / rect.width;
  percentage = Math.max(0, Math.min(1, percentage));

  // 0 to 10 ct range, 0.1 ct steps
  const value = Math.round(percentage * 10 * 10) / 10;
  currentCarat.value = value;
};

const startDragging = (e: MouseEvent | TouchEvent) => {
  isDragging.value = true;
  updateCaratFromEvent(e);
};

const handleMove = (e: MouseEvent | TouchEvent) => {
  if (isDragging.value) {
    updateCaratFromEvent(e);
  }
};

const stopDragging = () => {
  isDragging.value = false;
};

onMounted(() => {
  window.addEventListener("mousemove", handleMove);
  window.addEventListener("mouseup", stopDragging);
  window.addEventListener("touchmove", handleMove, { passive: false });
  window.addEventListener("touchend", stopDragging);
  window.addEventListener("resize", updateMobile);
});

onUnmounted(() => {
  window.removeEventListener("mousemove", handleMove);
  window.removeEventListener("mouseup", stopDragging);
  window.removeEventListener("touchmove", handleMove);
  window.removeEventListener("touchend", stopDragging);
  window.removeEventListener("resize", updateMobile);
});
</script>

<template>
  <div :class="['flex flex-col items-center ', isMobileMode ? '' : 'gap-8 lg:flex-row']">
    <!-- Left column: info text -->
    <div :class="['w-full text-left order-1', isMobileMode ? '' : 'lg:w-[34%] lg:order-1']">
      <slot name="title" :currentCarat="currentCarat">
        <h3 class="text-2xl font-bold mb-6">
          {{ t("components.diamonds.details.carat.title") }}: {{ $n(currentCarat) }} ct.
        </h3>
      </slot>
      <p class="text-black font-light text-sm leading-relaxed whitespace-pre-line">
        {{ t("components.diamonds.details.carat.text") }}
      </p>
    </div>

    <!-- Middle column: Slider -->
    <div :class="['w-full py-20 px-4 order-3', isMobileMode ? '' : 'lg:w-[51%] lg:order-2']">
      <div
        class="relative h-1 bg-brand-main/30 rounded-full cursor-pointer"
        ref="sliderRef"
        @mousedown="startDragging"
        @touchstart.prevent="startDragging"
      >
        <!-- Ticks and numbers -->
        <div class="absolute inset-0 flex justify-between">
          <div v-for="i in 11" :key="i" class="relative h-full flex flex-col items-center">
            <!-- Gap -->
            <div
                v-if=" i !== 1 && i !== 11"
              class="absolute -top-2 h-5 w-1 bg-white"
            ></div>
            <span class="absolute top-4 text-brand-main text-xs">{{ i - 1 }}</span>
          </div>
        </div>

        <!-- "Dein Diamant" or "Min" marker -->
        <div
          class="absolute -top-14 -translate-x-1/2 flex flex-col items-center transition-all duration-300"
          :style="{ left: (minCarat / 10) * 100 + '%' }"
        >
          <span class="text-brand-main italic text-[12px] leading-tight text-center whitespace-nowrap mb-1">
            {{ isRange ? t('components.diamonds.details.carat.min') : t('components.diamonds.details.carat.your_diamond') }}<br />
            {{ $n(minCarat) }} ct.
          </span>
          <div class="w-[1px] h-6 border-l border-dashed border-brand-main"></div>
        </div>

        <!-- "Max" marker (only if range) -->
        <div
          v-if="isRange"
          class="absolute -top-14 -translate-x-1/2 flex flex-col items-center transition-all duration-300"
          :style="{ left: (maxCarat / 10) * 100 + '%' }"
        >
          <span class="text-brand-main italic text-[12px] leading-tight text-center whitespace-nowrap mb-1">
            {{ t('components.diamonds.details.carat.max') }}<br />
            {{ $n(maxCarat) }} ct.
          </span>
          <div class="w-[1px] h-6 border-l border-dashed border-brand-main"></div>
        </div>

        <!-- Slider Handle -->
        <div
          class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 bg-brand-main rounded-full z-20 shadow-sm"
          :style="{ left: (currentCarat / 10) * 100 + '%' }"
        >
          <!-- Current Value Bubble -->
          <div class="absolute top-8 left-1/2 -translate-x-1/2 flex flex-col items-center">
            <div
              class="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[8px] border-b-brand-main"
            ></div>
            <div class="bg-brand-main text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap -mt-[1px]">
              {{ $n(currentCarat) }} ct.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right column: Diamond detail carat size component -->
    <div :class="['w-full flex justify-center items-center order-2', isMobileMode ? '' : 'lg:w-[15%] lg:order-3']">
      <diamond-detail-carat-size
        :carat="currentCarat"
        :form="form"
        :diamondWidth="displayDimensions.width"
        :diamondLength="displayDimensions.length"
      />
    </div>
  </div>
</template>
